import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'
import { changeVoiceAgentSlug } from '@/reducers/agentProvisioning/agentProvisioning.actions'
import { ERROR_KIND, SLUG_PATTERN } from '@/reducers/agentProvisioning/agentProvisioning.constants'

// Correct a slug an agent was provisioned under by mistake (AVR-886). Balto
// admins only: the change only fixes what the backend holds, so it needs a
// redeploy customers can't do when the agent's code calls a different slug, and
// it breaks promotion until every environment has the same slug.
const ChangeSlugModal = ({ show, onClose, voiceAgentId, currentSlug }) => {
  const dispatch = useDispatch()
  const saving = useSelector((state) => state.agentProvisioning.loading?.changeSlug)

  const [slug, setSlug] = useState('')
  const [saveError, setSaveError] = useState(null)
  // The BE's slug_not_deployed text, which may suggest a near miss. Set only
  // while the typed slug is the one it was about.
  const [unreleasedDetail, setUnreleasedDetail] = useState(null)

  // Also on an agent change: back/forward between two agents' Overview tabs
  // keeps this mounted, and a slug typed for one must never be saved to the other.
  useEffect(() => {
    if (show) {
      setSlug('')
      setSaveError(null)
      setUnreleasedDetail(null)
    }
  }, [show, voiceAgentId])

  const trimmed = slug.trim()
  const formatError =
    !isEmpty(trimmed) && !SLUG_PATTERN.test(trimmed)
      ? 'Use lowercase letters, digits, and hyphens only.'
      : null
  const canSave = !isEmpty(trimmed) && !formatError && trimmed !== currentSlug

  const save = async (allowUnreleasedSlug) => {
    if (!canSave || saving) {
      return
    }
    setSaveError(null)
    setUnreleasedDetail(null)
    const result = await dispatch(
      changeVoiceAgentSlug({ voiceAgentId, slug: trimmed, allowUnreleasedSlug })
    )
    if (result.ok) {
      onClose()
    } else if (!allowUnreleasedSlug && result.kind === ERROR_KIND.SLUG_NOT_DEPLOYED) {
      setUnreleasedDetail(result.message)
    } else {
      setSaveError(result.message)
    }
  }

  const onSlugChange = (event) => {
    setSlug(event.target.value)
    setUnreleasedDetail(null)
    setSaveError(null)
  }

  return (
    <BasicModal
      title="Change slug"
      show={show}
      onClose={onClose}
      size="small"
      footer={
        <>
          <Button onClick={onClose} data-testid="change-slug-cancel">
            Cancel
          </Button>
          <Button
            brand
            disabled={!canSave || saving}
            loading={saving}
            onClick={() => save(false)}
            data-testid="change-slug-save"
          >
            Save
          </Button>
        </>
      }
    >
      <ul className="voice-agents__change-slug-notes">
        <li>
          This changes the slug the backend holds. The agent must already call{' '}
          <code>/agent/&lt;new slug&gt;/...</code>, or it needs a redeploy with{' '}
          <code>ROUTER_NAME</code> set to the new slug.
        </li>
        <li>
          <strong>Promotion between environments.</strong> Prompt overrides are promoted by matching
          the slug. Make the same change on every environment that has this agent, or promotion for
          it will fail. Files exported before this change won&apos;t import; export again after the
          change.
        </li>
        <li>
          Running backend pods keep using the old slug for this agent until they restart, so the
          change takes effect with the next backend deploy.
        </li>
      </ul>
      <FormField label="Current slug" disabled>
        <TextInput aria-label="Current slug" value={currentSlug} disabled />
      </FormField>
      <FormField label="New slug" required error={Boolean(formatError)} disabled={saving}>
        <TextInput
          aria-label="New slug"
          value={slug}
          error={Boolean(formatError)}
          autoFocus
          disabled={saving}
          onChange={onSlugChange}
          data-testid="change-slug-input"
        />
        {formatError && <ErrorMessage content={formatError} />}
      </FormField>
      {!isNil(unreleasedDetail) && (
        <Banner warning header="Code not released here" data-testid="change-slug-unreleased">
          <p>{unreleasedDetail}</p>
          <Button
            loading={saving}
            disabled={saving}
            onClick={() => save(true)}
            data-testid="change-slug-confirm-unreleased"
          >
            Change anyway (code not released here yet)
          </Button>
        </Banner>
      )}
      {saveError && (
        <Banner critical header="Slug change failed" data-testid="change-slug-error">
          <p>{saveError}</p>
        </Banner>
      )}
    </BasicModal>
  )
}

export default ChangeSlugModal
